import type { Language, Translation } from '../index';

// Définir la structure de traduction sans auto-référence
export const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success'
  },
  cookies: {
    notice: 'We use cookies to improve your experience',
    description: 'We use essential cookies to make this site work. You can enable additional cookies to personalize your experience.',
    essential: 'Essential only',
    accept: 'Accept all'
  },
  auth: {
    signIn: 'Sign in',
    signUp: 'Sign up',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset password'
  },
  chat: {
    inputPlaceholder: 'Type your message...',
    welcomeMessage: 'Hello! How can I help you today?',
    sendMessage: 'Send',
    typing: 'Typing...'
  },
  settings: {
    general: 'General',
    design: 'Design',
    ai: 'AI Settings',
    miniApps: 'Mini Apps',
    profile: 'Profile',
    widget: 'Widget'
  },
  orderTracking: {
    quickActionText: 'Track my order',
    promptMessage: 'Please enter your order number to track your order:',
    errorMessage: 'Sorry, we couldn\'t find that order. Please check the order number and try again.',
    button: 'Track my order',
    orderStatus: {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    }
  },
  miniApps: {
    products: {
      title: 'Products',
      description: 'Browse our products',
      price: 'Price',
      stock: 'Stock'
    },
    calendar: {
      title: 'Calendar',
      description: 'Schedule an appointment',
      bookAppointment: 'Book'
    },
    services: {
      title: 'Services',
      description: 'Our services',
      learnMore: 'Learn more'
    },
    gallery: {
      title: 'Gallery',
      description: 'View our gallery'
    },
    realestate: {
      title: 'Real Estate',
      description: 'Browse properties',
      price: 'Price',
      surface: 'Surface area',
      rooms: 'Rooms',
      location: 'Location',
      type: 'Property type',
      status: 'Status',
      features: 'Features'
    }
  },
  pages: {
    home: {
      hero: {
        title: "Artificial Intelligence",
        subtitle: "empowering your users",
        description: "Discover the next generation of AI-powered chatbots. Benefit from smooth lead qualification and capture through our unique market-leading tools.",
        cta: {
          primary: 'Start for free',
          secondary: 'View pricing',
          dashboard: 'Access my Dashboard'
        }
      },
      demo: {
        title: 'Discover our Demo',
        description: 'Test our chatbot live and discover all its features'
      },
      leadDetection: {
        title: 'Lead detection and capture',
        highlight: 'automatic',
        suffix: ' ',
        description: 'Capture maximum data and deeply understand your visitors\' needs through our advanced analytics tools.',
        features: {
          emails: {
            title: 'Email Detection',
            description: 'Automatic capture of email addresses mentioned in conversations'
          },
          phones: {
            title: 'Phone Detection',
            description: 'Intelligent recognition of phone numbers in exchanges'
          },
          notifications: {
            title: 'Instant Notifications',
            description: 'Be alerted in real-time of each new lead detected'
          },
          crm: {
            title: 'CRM Integrations',
            description: 'Automatic synchronization with Salesforce, HubSpot and more'
          }
        }
      },
      design: {
        title: 'Ultra',
        highlight: 'customization',
        suffix: 'of your bot design!',
        description: 'Create a unique chatbot that perfectly reflects your visual identity. Our intuitive interface allows you to customize every aspect of the appearance.',
        features: {
          colors: 'Customizable color gradients',
          messages: 'Message and text customization',
          themes: 'Light and dark themes'
        }
      },
      ai: {
        title: 'A',
        highlight: 'smooth AI',
        suffix: 'setup',
        description: 'Configure your AI assistant in a few clicks. Our intuitive interface guides you through each step for optimal efficiency.',
        features: {
          personalization: 'Advanced AI personalization',
          security: 'Guaranteed security and confidentiality',
          multilingual: 'Multilingual support'
        }
      },
      miniApps: {
        title: ' ',
        highlight: 'Mini Apps',
        suffix: 'for all your needs!',
        description: 'Offer a unique and interactive experience to your customers. No need to change pages, everything is integrated directly into the chat!',
        apps: {
          calendar: {
            title: 'Calendar',
            description: 'Integrated appointment booking'
          },
          products: {
            title: 'Products',
            description: 'Interactive catalog'
          },
          services: {
            title: 'Services',
            description: 'Dynamic presentation'
          },
          gallery: {
            title: 'Gallery',
            description: 'Photos and videos'
          }
        }
      },
      multilingual: {
        title: 'Built-in',
        highlight: 'Multilingual',
        suffix: 'Support',
        description: 'Communicate with your customers in their language. Our chatbot automatically adapts to your visitors\' language.',
        features: {
          translation: 'Complete translation',
          choice: 'Give users the choice',
          configuration: 'Simple Configuration'
        }
      },
      analytics: {
        title: 'Learn about your',
        highlight: 'users',
        description: 'Capture maximum data and understand your visitors\' needs in depth.',
        features: {
          stats: {
            title: 'Advanced Statistics',
            description: 'Analyze your visitors\' behavior in detail and optimize your strategy.'
          },
          segments: {
            title: 'User Segmentation',
            description: 'Identify your most relevant user segments.'
          }
        }
      },
      linktree: {
        title: 'No more',
        highlight: 'LinkTree',
        suffix: '!',
        description: 'Capture more leads, make your brand or personal branding talk, literally! Energize your site and retain your users.',
        features: {
          sharing: {
            title: 'Interactive Sharing',
            description: 'Share your links in an interactive and engaging way with your visitors.'
          },
          conversion: {
            title: 'Optimized Conversion',
            description: 'Increase your conversion rates through a unique user experience.'
          },
          analytics: {
            title: 'Detailed Analytics',
            description: 'Track and analyze the performance of your shared links.'
          }
        }
      },
      integrations: {
        title: 'Powerful',
        highlight: 'Integrations',
        suffix: 'at your fingertips',
        badge: 'All-in-One Platform',
        subtitle: 'at your service',
        description: 'Connect your favorite tools and services seamlessly. Our platform integrates with the most popular business solutions.',
        features: {
          crm: {
            title: 'CRM Integration',
            description: 'Automatically sync lead data with Salesforce, HubSpot, and more'
          },
          analytics: {
            title: 'Analytics Integration',
            description: 'Track conversions and user behavior with popular analytics tools'
          },
          marketing: {
            title: 'Marketing Tools Integration',
            description: 'Connect with your email marketing and automation platforms'
          },
          automation: {
            title: 'Workflow Automation',
            description: 'Create powerful automations with Zapier, Make, and other workflow tools'
          },
          web: {
            title: 'Web Integration',
            description: 'Seamlessly integrate with your website and web applications'
          }
        }
      },
      ecommerce: {
        title: 'Complete',
        highlight: 'E-commerce',
        suffix: 'solution',
        description: 'Manage your online store effortlessly. From product management to order tracking, everything you need in one place.',
        features: {
          products: {
            title: 'Product Management',
            description: 'Easily manage and sync your product catalog'
          },
          orders: {
            title: 'Order Tracking',
            description: 'Real-time order status and tracking'
          },
          cart: {
            title: 'Cart Recovery',
            description: 'Automated abandoned cart recovery'
          },
          promotions: {
            title: 'Promotions',
            description: 'Create and manage promotional campaigns'
          }
        }
      }
    },
    pricing: {
      title: 'Choose your',
      subtitle: 'Botmaker plan',
      description: 'Plans for all your needs',
      monthly: 'Monthly',
      yearly: 'Yearly',
      free: 'Free',
      starter: 'Starter',
      professional: 'Professional',
      enterprise: 'Enterprise',
      features: {
        ai: 'AI Settings',
        design: 'Design Settings',
        widget: 'Integration codes',
        integrations: 'All mini-apps',
        gallery: 'Photo gallery',
        conversations: 'Conversations',
        stats: 'Statistics'
      },
      billing_info: 'All prices are in euros excluding tax. Billing is',
      conversations: {
        title: 'How do conversations work?',
        description: 'A conversation starts with the first user message and ends after a period of inactivity. Regardless of the number of exchanges (2 or 50 messages), it counts as a single conversation. Each plan includes a defined number of conversations per month, allowing you to interact extensively with your visitors.'
      },
      custom: {
        title: 'Need a custom plan?',
        description: 'Want a higher conversation volume or a 100% customized chatbot? Our team is here to understand your needs and propose a tailored solution.',
        cta: 'Schedule a meeting'
      }
    }
  },
  footer: {
    product: {
      title: 'Product',
      features: 'Features',
      pricing: 'Pricing',
      guide: 'Guide',
      miniApps: 'Mini Apps',
      integrations: 'Integrations'
    },
    resources: {
      title: 'Resources',
      documentation: 'Documentation',
      blog: 'Blog'
    },
    company: {
      title: 'Company',
      about: 'About',
      contact: 'Contact',
      careers: 'Careers',
      partners: 'Partners'
    },
    legal: {
      title: 'Legal',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      cookies: 'Cookies',
      mentions: 'Legal Notice'
    },
    copyright: '© 2025 Botmaker Agency. All rights reserved.'
  }
};